import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { ProfileDropdown } from './ProfileDropdown';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  UserCheck, 
  Plus, 
  Search, 
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  ClipboardList,
  Shield,
  Settings,
  X
} from 'lucide-react';

export function EventDashboard() {
  const { user } = useAuth();
  const [activeMainTab, setActiveMainTab] = useState('leadership');
  const [activeSubTab, setActiveSubTab] = useState('race-events');
  const [selectedTile, setSelectedTile] = useState('officials'); // Default to officials tile
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  
  // Auto-select buttons when tiles are selected
  React.useEffect(() => {
    if (selectedTile === 'events') {
      setActiveMainTab('leadership');
      setActiveSubTab('race-events');
    } else if (selectedTile === 'officials') {
      setActiveMainTab('judging');
      setActiveSubTab('judging');
    }
  }, [selectedTile]);

  // Mock data - replace with actual data from your database
  const raceEvents = [
    { id: 1, name: 'HYROX Mumbai 2025', date: 'Sep 7, 2025', location: 'NESCO Hall 6', status: 'Active', participants: 3300 },
    { id: 2, name: 'HYROX Bengaluru 2026', date: 'Apr 11, 2026', location: 'BIEC Expo Hall', status: 'Upcoming', participants: 4000 },
    { id: 3, name: 'HYROX Delhi 2025', date: '2024-06-10', location: 'Yashobhoomi Hall B', status: 'Completed', participants: 2600 }
  ];

  const raceOfficials = [
    { 
      firstname: 'John', 
      lastname: 'Smith', 
      email: 'john.smith@race.com', 
      roleid: 1,
      status: 'Active', 
      events: 5, 
      phonenumber: 1234567890, 
      isactive: true,
      officialuuid: 'uuid-1',
      supervisorid: null,
      lastlogin: null,
      notes: null,
      createdat: '2024-01-01',
      createdby: 'admin'
    },
    { 
      firstname: 'Sarah', 
      lastname: 'Johnson', 
      email: 'sarah.j@race.com', 
      roleid: 2,
      status: 'Active', 
      events: 3, 
      phonenumber: 1234567891, 
      isactive: true,
      officialuuid: 'uuid-2',
      supervisorid: 1,
      lastlogin: null,
      notes: null,
      createdat: '2024-01-01',
      createdby: 'admin'
    },
    { 
      firstname: 'Mike', 
      lastname: 'Davis', 
      email: 'mike.davis@race.com',  
      roleid: 3,
      status: 'Active', 
      events: 1, 
      phonenumber: 1234567892, 
      isactive: true,
      officialuuid: 'uuid-3',
      supervisorid: 1,
      lastlogin: null,
      notes: null,
      createdat: '2024-01-01',
      createdby: 'admin'
    },
    { 
      firstname: 'Lisa', 
      lastname: 'Wilson', 
      email: 'lisa.wilson@race.com', 
      roleid: 2,
      status: 'Inactive', 
      events: 0, 
      phonenumber: 1234567893, 
      isactive: false,
      officialuuid: 'uuid-4',
      supervisorid: 1,
      lastlogin: null,
      notes: null,
      createdat: '2024-01-01',
      createdby: 'admin'
    }
  ];

  const otherUsers = [
    { 
      firstname: 'Admin', 
      lastname: 'User', 
      email: 'admin@race.com', 
      status: 'Active',
      roleid: 1,
      isactive: true,
      phonenumber: null,
      events: 0,
      officialuuid: 'uuid-admin',
      supervisorid: null,
      lastlogin: null,
      notes: null,
      createdat: '2024-01-01',
      createdby: 'system'
    },
    {  
      firstname: 'Event', 
      lastname: 'Coordinator', 
      email: 'coordinator@race.com', 
      status: 'Active',
      roleid: 3,
      isactive: true,
      phonenumber: null,
      events: 0,
      officialuuid: 'uuid-coord',
      supervisorid: null,
      lastlogin: null,
      notes: null,
      createdat: '2024-01-01',
      createdby: 'admin'
    },
    { 
      firstname: 'Support', 
      lastname: 'Staff', 
      email: 'support@race.com', 
      status: 'Active',
      roleid: 4,
      isactive: true,
      phonenumber: null,
      events: 0,
      officialuuid: 'uuid-change',
      supervisorid: null,
      lastlogin: null,
      notes: null,
      createdat: '2024-01-01',
      createdby: 'admin'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-hyrox-blue bg-blue-50 border border-hyrox-blue/20';
      case 'upcoming':
        return 'text-hyrox-orange bg-orange-50 border border-hyrox-orange/20';
      case 'completed':
        return 'text-gray-600 bg-gray-100 border border-gray-200';
      case 'inactive':
        return 'text-red-600 bg-red-50 border border-red-200';
      default:
        return 'text-gray-600 bg-gray-100 border border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-hyrox-light">
      <header className="bg-hyrox-dark border-b border-gray-800 px-6 py-4 shadow-lg">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-6">
                         <Link to="/dashboard">
               <img 
                 src="/HyroxLogo.png" 
                 alt="Hyrox Logo" 
                 className="h-7 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
               />
             </Link>
            <Link 
              to="/penalty-logging" 
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-300 hover:text-hyrox-white hover:bg-hyrox-gray rounded-md transition-colors"
            >
              <ClipboardList className="h-4 w-4" />
              Penalty Logger
            </Link>
            <Link 
              to="/head-judge-dashboard" 
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-300 hover:text-hyrox-white hover:bg-hyrox-gray rounded-md transition-colors"
            >
              <Shield className="h-4 w-4" />
              Head Judge
            </Link>
          </div>
          <div className="ml-8">
            <ProfileDropdown />
          </div>
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="grid grid-cols-2 gap-6 mb-4">
            <Card 
              className={`hover:shadow-lg transition-shadow border cursor-pointer ${
                selectedTile === 'events' 
                  ? 'border-hyrox-blue bg-blue-50' 
                  : 'border-gray-200 bg-white'
              }`}
              onClick={() => setSelectedTile('events')}
            >
              <CardContent className="p-4 flex flex-col items-center justify-center">
                <h2 className="text-3xl font-bold text-hyrox-text">Events</h2>
              </CardContent>
            </Card>
            
            <Card 
              className={`hover:shadow-lg transition-shadow border cursor-pointer ${
                selectedTile === 'officials' 
                  ? 'border-hyrox-blue bg-blue-50' 
                  : 'border-gray-200 bg-white'
              }`}
              onClick={() => setSelectedTile('officials')}
            >
              <CardContent className="p-4 flex flex-col items-center justify-center">
                <h2 className="text-3xl font-bold text-hyrox-text">Officials</h2>
              </CardContent>
            </Card>
          </div>
          
                     {/* Buttons Section - Full Width */}
           {selectedTile === 'events' && (
             <div className="grid grid-cols-2 gap-2 max-w-md mx-auto">
               <Button 
                 variant={activeSubTab === 'race-events' ? 'default' : 'outline'}
                 className="flex items-center gap-2"
                 onClick={() => {
                   setActiveMainTab('leadership');
                   setActiveSubTab('race-events');
                 }}
               >
                 <Calendar className="h-4 w-4" />
                 Race Events
               </Button>              
               <Button 
                 variant={activeSubTab === 'leadership' ? 'default' : 'outline'}
                 className="flex items-center gap-2"
                 onClick={() => {
                   setActiveMainTab('leadership');
                   setActiveSubTab('leadership');
                 }}
               >
                 <Shield className="h-4 w-4" />
                 Leadership
               </Button>
             </div>
           )}
          
          {selectedTile === 'officials' && (
            <div className="grid grid-cols-3 gap-2 max-w-lg mx-auto">
              <Button 
                variant={activeMainTab === 'judging' ? 'default' : 'outline'}
                className="flex items-center gap-2"
                onClick={() => setActiveMainTab('judging')}
              >
                <UserCheck className="h-4 w-4" />
                Judging
              </Button>
                             <Button 
                 variant={(activeMainTab === 'leadership' && activeSubTab === 'other-users') ? 'default' : 'outline'}
                 className="flex items-center gap-2"
                 onClick={() => {
                   setActiveMainTab('leadership');
                   setActiveSubTab('other-users');
                 }}
               >
                 <Users className="h-4 w-4" />
                 Official Users
               </Button>
              <Button 
                variant={activeMainTab === 'operations' ? 'default' : 'outline'}
                className="flex items-center gap-2"
                onClick={() => setActiveMainTab('operations')}
              >
                <Settings className="h-4 w-4" />
                Operations
              </Button>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="mt-8">
                     {/* Leadership Tab */}
           {(activeMainTab === 'leadership') && (
             <div className="space-y-6">
                               {/* Race Events Content - Show when Race Events button is clicked */}
                {(activeSubTab === 'race-events') && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-semibold text-hyrox-text">Race Events</h3>
                      <Button className="flex items-center gap-2 bg-hyrox-blue hover:bg-hyrox-blue/90">
                        <Plus className="h-4 w-4" />
                        Add Event
                      </Button>
                    </div>

                    <div className="grid gap-6">
                      {raceEvents.map((event) => (
                        <Card key={event.id} className="hover:shadow-lg transition-shadow border border-gray-200">
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-xl text-hyrox-text">{event.name}</CardTitle>
                                <p className="text-hyrox-text-light mt-1">{event.location}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                                  {event.status}
                                </span>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-hyrox-text">Date:</span>
                                <p className="text-hyrox-text-light">{new Date(event.date).toLocaleDateString()}</p>
                              </div>
                              <div>
                                <span className="font-medium text-hyrox-text">Participants:</span>
                                <p className="text-hyrox-text-light">{event.participants}</p>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-4">
                              <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-blue text-hyrox-blue hover:bg-hyrox-blue hover:text-white">
                                <Edit className="h-3 w-3" />
                                Edit
                              </Button>
                                                             <Button 
                                 variant="outline" 
                                 size="sm" 
                                 className="flex items-center gap-2 border-hyrox-orange text-hyrox-orange hover:bg-hyrox-orange hover:text-white"
                                 onClick={() => {
                                   setSelectedEvent(event);
                                   setIsModalOpen(true);
                                 }}
                               >
                                 <Users className="h-3 w-3" />
                                 Read More...
                               </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Leadership Content - Show when Leadership button is clicked */}
                {(activeSubTab === 'leadership') && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-semibold text-hyrox-text">Leaders</h3>
                      <Button className="flex items-center gap-2 bg-hyrox-blue hover:bg-hyrox-blue/90">
                        <Plus className="h-4 w-4" />
                        Add Leader
                      </Button>
                    </div>

                    <div className="grid gap-6">
                      <Card className="hover:shadow-lg transition-shadow border border-gray-200">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-xl text-hyrox-text">Race Director</CardTitle>
                              <p className="text-hyrox-text-light mt-1">Oversees race operations and strategic planning</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 rounded-full text-xs font-medium text-hyrox-blue bg-blue-50 border border-hyrox-blue/20">
                                Active
                              </span>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-hyrox-text">Role:</span>
                              <p className="text-hyrox-text-light">Race Director</p>
                            </div>
                            <div>
                              <span className="font-medium text-hyrox-text">Events Managed:</span>
                              <p className="text-hyrox-text-light">12</p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-blue text-hyrox-blue hover:bg-hyrox-blue hover:text-white">
                              <Edit className="h-3 w-3" />
                              Edit
                            </Button>
                            <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-orange text-hyrox-orange hover:bg-hyrox-orange hover:text-white">
                              <Users className="h-3 w-3" />
                              Manage Team
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="hover:shadow-lg transition-shadow border border-gray-200">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-xl text-hyrox-text">Event Director</CardTitle>
                              <p className="text-hyrox-text-light mt-1">Manages individual event execution and logistics</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 rounded-full text-xs font-medium text-hyrox-blue bg-blue-50 border border-hyrox-blue/20">
                                Active
                              </span>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-hyrox-text">Role:</span>
                              <p className="text-hyrox-text-light">Event Director</p>
                            </div>
                            <div>
                              <span className="font-medium text-hyrox-text">Events Managed:</span>
                              <p className="text-hyrox-text-light">8</p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-blue text-hyrox-blue hover:bg-hyrox-blue hover:text-white">
                              <Edit className="h-3 w-3" />
                              Edit
                            </Button>
                            <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-orange text-hyrox-orange hover:bg-hyrox-orange hover:text-white">
                              <Users className="h-3 w-3" />
                              Manage Team
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

               {/* Users List Content - Show when Official Users button is clicked */}
               {(activeSubTab === 'other-users') && (
                 <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-semibold text-hyrox-text">Official Users</h3>
                      <Button className="flex items-center gap-2 bg-hyrox-blue hover:bg-hyrox-blue/90">
                        <Plus className="h-4 w-4" />
                        Add User
                      </Button>
                    </div>

                    <div className="grid gap-6">
                      {otherUsers.map((user) => (
                                               <Card key={user.officialuuid} className="hover:shadow-lg transition-shadow border border-gray-200">
                         <CardHeader>
                           <div className="flex justify-between items-start">
                             <div>
                               <CardTitle className="text-xl text-hyrox-text">{user.firstname} {user.lastname}</CardTitle>
                                <p className="text-hyrox-text-light mt-1">{user.email}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                                  {user.status}
                                </span>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-hyrox-text">Role:</span>
                                <p className="text-hyrox-text-light">{user.role}</p>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-4">
                              <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-blue text-hyrox-blue hover:bg-hyrox-blue hover:text-white">
                                <Edit className="h-3 w-3" />
                                Edit
                              </Button>
                              <Button variant="outline" size="sm" className="flex items-center gap-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white">
                                <Trash2 className="h-3 w-3" />
                                Remove
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Judging Tab */}
          {(activeMainTab === 'judging') && (
            <div className="space-y-6">
              <div className="mb-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-hyrox-text">Race Judges</h3>
                  <Button className="flex items-center gap-2 bg-hyrox-blue hover:bg-hyrox-blue/90">
                    <Plus className="h-4 w-4" />
                    Add Judge
                  </Button>
                </div>

                <div className="grid gap-6 mt-6">
                  {raceOfficials.map((official) => (
                                         <Card key={official.officialuuid} className="hover:shadow-lg transition-shadow border border-gray-200">
                       <CardHeader>
                         <div className="flex justify-between items-start">
                           <div>
                             <CardTitle className="text-xl text-hyrox-text">{official.firstname} {official.lastname}</CardTitle>
                            <p className="text-hyrox-text-light mt-1">{official.email}</p>
                            <p className="text-sm text-gray-500 mt-1">{official.phonenumber}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(official.status)}`}>
                              {official.status}
                            </span>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-hyrox-text">Role:</span>
                            <p className="text-hyrox-text-light">{official.role}</p>
                          </div>
                          <div>
                            <span className="font-medium text-hyrox-text">Events Assigned:</span>
                            <p className="text-hyrox-text-light">{official.events}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-blue text-hyrox-blue hover:bg-hyrox-blue hover:text-white">
                            <Edit className="h-3 w-3" />
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-orange text-hyrox-orange hover:bg-hyrox-orange hover:text-white">
                            <Calendar className="h-3 w-3" />
                            Assign Events
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Operations Tab */}
          {(activeMainTab === 'operations') && (
            <div className="space-y-6">
              <div className="mb-6">
                <div className="grid gap-6">
                  <Card className="hover:shadow-lg transition-shadow border border-gray-200">
                    <CardHeader>
                      <CardTitle className="text-xl text-hyrox-text">System Settings</CardTitle>
                      <p className="text-hyrox-text-light">Configure application settings and preferences</p>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-blue text-hyrox-blue hover:bg-hyrox-blue hover:text-white">
                          <Settings className="h-3 w-3" />
                          Configure
                        </Button>
                        <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-orange text-hyrox-orange hover:bg-hyrox-orange hover:text-white">
                          <Filter className="h-3 w-3" />
                          Filters
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-lg transition-shadow border border-gray-200">
                    <CardHeader>
                      <CardTitle className="text-xl text-hyrox-text">Reports & Analytics</CardTitle>
                      <p className="text-hyrox-text-light">View system reports and performance metrics</p>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-blue text-hyrox-blue hover:bg-hyrox-blue hover:text-white">
                          <ClipboardList className="h-3 w-3" />
                          Generate Report
                        </Button>
                        <Button variant="outline" size="sm" className="flex items-center gap-2 border-hyrox-orange text-hyrox-orange hover:bg-hyrox-orange hover:text-white">
                          <Search className="h-3 w-3" />
                          View Analytics
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
                 </div>
       </div>

       {/* Modal */}
       {isModalOpen && selectedEvent && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
             <div className="flex justify-between items-start mb-4">
               <h2 className="text-2xl font-bold text-hyrox-text">{selectedEvent.name}</h2>
               <Button
                 variant="ghost"
                 size="sm"
                 onClick={() => setIsModalOpen(false)}
                 className="text-gray-500 hover:text-gray-700"
               >
                 <X className="h-5 w-5" />
               </Button>
             </div>
             
             <div className="space-y-4">
               <div className="grid grid-cols-2 gap-4 text-sm">
                 <div>
                   <span className="font-medium text-hyrox-text">Location:</span>
                   <p className="text-hyrox-text-light">{selectedEvent.location}</p>
                 </div>
                 <div>
                   <span className="font-medium text-hyrox-text">Date:</span>
                   <p className="text-hyrox-text-light">{new Date(selectedEvent.date).toLocaleDateString()}</p>
                 </div>
                 <div>
                   <span className="font-medium text-hyrox-text">Participants:</span>
                   <p className="text-hyrox-text-light">{selectedEvent.participants}</p>
                 </div>
                 <div>
                   <span className="font-medium text-hyrox-text">Status:</span>
                   <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedEvent.status)}`}>
                     {selectedEvent.status}
                   </span>
                 </div>
               </div>
               
               <div className="border-t pt-4">
                 <h3 className="text-lg font-semibold text-hyrox-text mb-2">Event Details</h3>
                 <p className="text-hyrox-text-light leading-relaxed">
                   {selectedEvent.name} is a premier HYROX competition featuring {selectedEvent.participants} participants 
                   competing in a series of challenging functional fitness stations. The event will be held at {selectedEvent.location} 
                   and promises to deliver an intense, high-energy experience for both athletes and spectators.
                 </p>
                 
                 <div className="mt-4">
                   <h4 className="font-medium text-hyrox-text mb-2">Event Schedule</h4>
                   <ul className="text-hyrox-text-light space-y-1">
                     <li>• Registration & Check-in: 6:00 AM - 7:30 AM</li>
                     <li>• Athlete Briefing: 7:45 AM - 8:00 AM</li>
                     <li>• Competition Start: 8:15 AM</li>
                     <li>• Awards Ceremony: 4:00 PM</li>
                   </ul>
                 </div>
                 
                 <div className="mt-4">
                   <h4 className="font-medium text-hyrox-text mb-2">Competition Format</h4>
                   <p className="text-hyrox-text-light">
                     The competition consists of 8 functional fitness stations including rowing, sled push/pull, 
                     burpee broad jumps, and more. Athletes will complete all stations in sequence with their 
                     total time determining their final ranking.
                   </p>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 }
